import React, { useCallback } from 'react';

import styled from 'styled-components';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import { showBasicSnackBar } from '@aquariuxfintech/aqx-trader-react-ui/snack-bar';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useRequestOtpTimer } from '@aqx-core/common/hooks/sign-up/use-request-otp-timer';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { forgotPasswordViewController, useForgotPassword } from '@aqx-core/common/services/forgot-password';
import { EUISnackBarMessageType } from '@aqx-core/common/services/snackbar';
import { openUrl } from '@aqx-member/web/utils/url/open-url';

import AuthCompletedContent from '../../shared/completed-content';
import AuthResendText from '../../shared/resend-text';

const ForgotPasswordComplete: FCC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyProfile = useCompanyProfile();
  const { webtraderContactSupportUrl } = companyProfile || {};

  const { email = '', metatraderId = '', loading } = useForgotPassword();

  const { remainingTimeText, isResendDisabled, startTimer } = useRequestOtpTimer({
    isSubmitting: loading,
    durationSeconds: 60,
    identifier: email
  });

  const onClickLogInButton = useCallback(() => {
    navigate(AUTHENTICATION_ROUTES.LOGIN);
  }, [navigate]);

  const description = (
    <StyledDescription typography={ETypography.TEXT_XL_REGULAR}>
      {t('We have sent a password reset link to your email')}{' '}
      <StyledEmailText typography={ETypography.TEXT_XL_SEMIBOLD}>{email}</StyledEmailText>
      {'. '}
      {t('If you have not received it, please contact our support team.')}
    </StyledDescription>
  );

  const onClickSupportButton = () => {
    openUrl(webtraderContactSupportUrl, '_blank');
  };

  const onClickResend = useCallback(() => {
    const params = {
      email,
      metatraderId
    };
    const onError = (message: string) =>
      showBasicSnackBar({
        message,
        messageType: EUISnackBarMessageType.ERROR
      });

    const onSuccess = () => {
      startTimer();
    };
    forgotPasswordViewController.sendForgotPasswordEmail({ params, onError, onSuccess });
  }, [email, metatraderId, startTimer]);

  return (
    <>
      <AuthCompletedContent
        header={t('Help is on the way!')}
        descriptionNode={description}
        onClickPrimaryButton={onClickLogInButton}
        primaryButtonText={t('Back to Log In')}
        primaryButtonDataTestId={EDataTestId.FORGOT_PASSWORD_BACK_TO_LOG_IN}
        secondaryButtonText={t('Contact Support')}
        secondaryButtonDataTestId={EDataTestId.FORGOT_PASSWORD_CONTACT_US}
        onClickSecondaryButton={onClickSupportButton}
      />
      <AuthResendText
        descriptionText={t('Don’t receive the email?')}
        actionText={t('Resend email')}
        $marginTop={ESpacing.XXL}
        onClickResend={onClickResend}
        isResendDisabled={isResendDisabled}
        remainingTimeText={remainingTimeText}
      />
    </>
  );
};

export default ForgotPasswordComplete;

const StyledDescription = styled(UIText)`
  text-align: center;
  display: inline;
  color: ${({ theme }) => theme.themeColors.text.subtle};
`;

const StyledEmailText = styled(UIText)`
  display: inline;
  color: ${({ theme }) => theme.themeColors.text.subtle};
`;
